/**
 * TopologyImportModal — Import airport topology.
 *
 * Three tabs: AIP PDF · GeoJSON · AIXM
 *
 * AIP PDF tab includes an optional manual GCP picker for CAD plans that lack
 * embedded DMS coordinate labels (e.g. AITSA "Plano de Aeródromo - TUM.pdf").
 *
 * The PDF is rendered client-side with PDF.js at 2× native resolution so the
 * image stays crisp at any zoom level without re-rendering.
 * Pan: drag. Zoom: scroll-wheel (zoom-to-cursor) or ± buttons.
 *
 * GCP arming: clicking a GCP row in the left panel arms that slot; any click
 * on the plan canvas then assigns its PDF coordinates. Dragging the map never
 * assigns a GCP, even when a slot is armed.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist';
import {
  X,
  UploadSimple,
  FilePdf,
  FileCode,
  FileJs,
  CheckCircle,
  WarningCircle,
  CircleNotch,
  Crosshair,
  Eye,
  ToggleLeft,
  ToggleRight,
  MapPin,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  ArrowsOut,
} from '@phosphor-icons/react';
import { conopsService } from '../../services/conops';
import type { ImportTopologyResponse } from '../../types/conops';

// PDF.js worker — resolved by Vite at build time
GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).href;

// Canvas render scale: 2× native PDF resolution.
// With a typical fit-zoom of ~0.2, the user can zoom to ~10× before any
// pixelation is visible — more than enough for clicking runway thresholds.
const PDF_RENDER_SCALE = 2.0;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TabId = 'aip_pdf' | 'geojson' | 'aixm';

interface GcpRow {
  label: string;
  wgs84: [number, number]; // [lon, lat]
  pdfX: number | null;
  pdfY: number | null;
}

interface Props {
  icaoCode: string;
  onImported: () => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------

const TAB_LABELS: Record<TabId, string> = {
  aip_pdf: 'AIP PDF',
  geojson: 'GeoJSON',
  aixm:    'AIXM',
};

const TAB_DESCRIPTIONS: Record<TabId, string> = {
  aip_pdf:
    'Sube el PDF del AIP AD2 oficial. Se extraen los paths vectoriales del plano de aeródromo mediante homografía 4-GCP → WGS-84. Requiere que el PDF contenga gráficos vectoriales (no escaneado).',
  geojson:
    'Sube un GeoJSON FeatureCollection digitalizado en QGIS u otra herramienta CAD. Cada feature debe tener la propiedad aeroway = runway | taxiway | apron | parking_position. Esta es la forma más precisa de definir la topología.',
  aixm:
    'Sube un fichero AIXM 4.5 o 5.1. La versión se detecta automáticamente desde los namespaces XML. Coordenadas ya en WGS-84, sin necesidad de GCPs.',
};

const MPTO_DEFAULT_GCPS: Omit<GcpRow, 'pdfX' | 'pdfY'>[] = [
  { label: 'THR 03R', wgs84: [-79.386944, 9.049444] },  // AIP: 09°02'58"N 079°23'13"W
  { label: 'THR 21L', wgs84: [-79.372622, 9.072976] },  // derived: 03R + 031° × 3048m
  { label: 'THR 03L', wgs84: [-79.393056, 9.047500] },  // AIP: 09°02'51"N 079°23'35"W
  { label: 'THR 21R', wgs84: [-79.378889, 9.071111] },  // AIP: 09°04'16"N 079°22'44"W
  { label: 'ARP',     wgs84: [-79.383333, 9.070000] },  // AIP AD2.2: 09°04'12"N 079°23'00"W
];

function makeDefaultGcps(icao: string): GcpRow[] {
  const base = icao.toUpperCase() === 'MPTO'
    ? MPTO_DEFAULT_GCPS
    : Array.from({ length: 4 }, (_, i) => ({
        label: `GCP ${i + 1}`,
        wgs84: [0, 0] as [number, number],
      }));
  return base.map(g => ({ ...g, pdfX: null, pdfY: null }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TopologyImportModal: React.FC<Props> = ({ icaoCode, onImported, onClose }) => {

  // ── Tab / file ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabId>('aip_pdf');
  const [file, setFile]           = useState<File | null>(null);
  const [pageHint, setPageHint]   = useState<number | undefined>(undefined);
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState<ImportTopologyResponse | null>(null);
  const [error, setError]         = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── GCP picker ────────────────────────────────────────────────────────────
  const [gcpMode, setGcpMode]           = useState(false);
  const [gcpRows, setGcpRows]           = useState<GcpRow[]>(() => makeDefaultGcps(icaoCode));
  const [activeGcpIdx, setActiveGcpIdx] = useState<number | null>(null); // null = not armed
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  // previewDims: native PDF page size in points (scale=1.0)
  const [previewDims, setPreviewDims] = useState<{ pdfW: number; pdfH: number } | null>(null);

  // ── PDF.js internals ──────────────────────────────────────────────────────
  const pdfDocRef    = useRef<PDFDocumentProxy | null>(null);
  const pdfPageRef   = useRef<PDFPageProxy | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const canvasRef    = useRef<HTMLCanvasElement>(null);

  // ── Zoom / pan (direct DOM writes — no re-render on mouse-move/wheel) ─────
  const containerRef   = useRef<HTMLDivElement>(null);
  const imgWrapRef     = useRef<HTMLDivElement>(null);
  const zoomDisplayRef = useRef<HTMLSpanElement>(null);
  const viewRef        = useRef({ zoom: 1, panX: 0, panY: 0 });

  // Drag state and click-vs-drag detection
  const dragRef       = useRef<{
    startX: number; startY: number;
    panX: number;   panY: number;
  } | null>(null);
  const lastWasDragRef = useRef(false); // persists through mouseup so onClick can read it
  const [isDragging, setIsDragging] = useState(false);

  // ── Derived ───────────────────────────────────────────────────────────────
  const completedGcps = gcpRows.filter(r => r.pdfX !== null).length;
  const canImport     = !!file && (!gcpMode || completedGcps >= 4);
  const isWide        = gcpMode && previewLoaded;

  const accept =
    activeTab === 'aip_pdf'  ? '.pdf,application/pdf' :
    activeTab === 'geojson'  ? '.geojson,.json,application/json' :
                               '.xml,text/xml,application/xml';

  const Icon =
    activeTab === 'aip_pdf' ? FilePdf :
    activeTab === 'geojson' ? FileJs :
                              FileCode;

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      renderTaskRef.current?.cancel();
      pdfDocRef.current?.destroy();
    };
  }, []);

  // ── Global mouseup (release drag when mouse leaves browser) ───────────────
  useEffect(() => {
    function onGlobalMouseUp() {
      if (dragRef.current) {
        dragRef.current = null;
        setIsDragging(false);
      }
    }
    window.addEventListener('mouseup', onGlobalMouseUp);
    return () => window.removeEventListener('mouseup', onGlobalMouseUp);
  }, []);

  // ── Non-passive wheel (zoom-to-cursor) ────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !previewLoaded) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const rect = el!.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const { zoom, panX, panY } = viewRef.current;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const newZoom = Math.min(Math.max(zoom * factor, 0.05), 20);
      const ratio   = newZoom / zoom;
      applyTransform(newZoom, mx - (mx - panX) * ratio, my - (my - panY) * ratio);
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [previewLoaded]);

  // ── Transform helpers (direct DOM, no re-render) ──────────────────────────

  function applyTransform(zoom: number, panX: number, panY: number) {
    viewRef.current = { zoom, panX, panY };
    if (imgWrapRef.current) {
      imgWrapRef.current.style.transform = `translate(${panX}px,${panY}px) scale(${zoom})`;
    }
    if (zoomDisplayRef.current) {
      zoomDisplayRef.current.textContent = `${Math.round(zoom * 100)}%`;
    }
  }

  function fitToContainer() {
    requestAnimationFrame(() => {
      const container = containerRef.current;
      const canvas    = canvasRef.current;
      if (!container || !canvas || !canvas.width) return;
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      const fitZoom = Math.min(cw / canvas.width, ch / canvas.height);
      applyTransform(fitZoom, (cw - canvas.width * fitZoom) / 2, (ch - canvas.height * fitZoom) / 2);
    });
  }

  function zoomBy(factor: number) {
    const { zoom, panX, panY } = viewRef.current;
    const el = containerRef.current;
    if (!el) return;
    const cx = el.clientWidth  / 2;
    const cy = el.clientHeight / 2;
    const newZoom = Math.min(Math.max(zoom * factor, 0.05), 20);
    const ratio   = newZoom / zoom;
    applyTransform(newZoom, cx - (cx - panX) * ratio, cy - (cy - panY) * ratio);
  }

  // ── PDF.js render ─────────────────────────────────────────────────────────

  async function loadPreview() {
    if (!file) return;
    setPreviewLoading(true);
    try {
      // Cancel any in-progress render from a previous load
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
      pdfDocRef.current?.destroy();
      pdfDocRef.current = null;

      const buffer = await file.arrayBuffer();
      const pdf    = await getDocument({ data: buffer }).promise;
      pdfDocRef.current = pdf;

      const pdfPageNum = pageHint ?? 1; // PDF.js is 1-based
      const page = await pdf.getPage(pdfPageNum);
      pdfPageRef.current = page;

      // Native page dimensions (scale=1.0) — used for GCP coordinate mapping
      const nativeVp = page.getViewport({ scale: 1.0 });
      setPreviewDims({ pdfW: nativeVp.width, pdfH: nativeVp.height });

      // Render to canvas at 2× native resolution
      const renderVp = page.getViewport({ scale: PDF_RENDER_SCALE });
      const canvas   = canvasRef.current!;
      canvas.width   = renderVp.width;
      canvas.height  = renderVp.height;
      const task = page.render({ canvas, viewport: renderVp });
      renderTaskRef.current = task;
      await task.promise;
      renderTaskRef.current = null;

      // GCP picker: start with no slot armed (user must click a row to arm)
      setActiveGcpIdx(null);
      setPreviewLoaded(true);
      applyTransform(1, 0, 0); // fitToContainer fires from canvas onLoad equivalent below
    } catch (err) {
      const name = (err as Error)?.name;
      if (name !== 'RenderingCancelledException') {
        console.error('[PDF.js] render failed:', err);
      }
    } finally {
      setPreviewLoading(false);
    }
  }

  // Fit after canvas is painted (triggered via useLayoutEffect watching previewLoaded)
  useEffect(() => {
    if (previewLoaded) fitToContainer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewLoaded]);

  // ── Drag-to-pan ───────────────────────────────────────────────────────────

  function handleMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    e.preventDefault();
    lastWasDragRef.current = false; // reset at start of every new gesture
    const { panX, panY } = viewRef.current;
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX, panY };
    setIsDragging(true);
  }

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (!lastWasDragRef.current && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
      lastWasDragRef.current = true; // mark as drag; persists into onClick
    }
    if (lastWasDragRef.current) {
      applyTransform(viewRef.current.zoom, dragRef.current.panX + dx, dragRef.current.panY + dy);
    }
  }

  function handleMouseUp() {
    dragRef.current = null;
    setIsDragging(false);
    // lastWasDragRef intentionally NOT cleared here —
    // onClick fires after mouseup and still needs to read it
  }

  // ── GCP click on canvas ───────────────────────────────────────────────────

  const handleGcpClickOnCanvas = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // Skip if: no slot armed, no dims, or this mouse-down-up was actually a drag
    if (activeGcpIdx === null || !previewDims || lastWasDragRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    // rect already accounts for CSS transform; dividing by rect dimensions
    // gives the correct fraction of the native PDF page regardless of zoom.
    const pdfX = ((e.clientX - rect.left) / rect.width)  * previewDims.pdfW;
    const pdfY = ((e.clientY - rect.top)  / rect.height) * previewDims.pdfH;

    const captured = activeGcpIdx;
    setGcpRows(prev => {
      const next = prev.map((row, i) => i === captured ? { ...row, pdfX, pdfY } : row);
      // Auto-arm next incomplete slot
      const nextIdx = next.findIndex((r, i) => i > captured && r.pdfX === null);
      setActiveGcpIdx(nextIdx >= 0 ? nextIdx : null);
      return next;
    });
  }, [activeGcpIdx, previewDims]);

  // ── Tab / file handlers ───────────────────────────────────────────────────

  function resetPreview() {
    renderTaskRef.current?.cancel();
    pdfDocRef.current?.destroy();
    pdfDocRef.current = null;
    pdfPageRef.current = null;
    setPreviewLoaded(false);
    setPreviewDims(null);
    setPreviewLoading(false);
    setActiveGcpIdx(null);
  }

  function handleTabChange(tab: TabId) {
    setActiveTab(tab);
    setFile(null);
    setPageHint(undefined);
    setResult(null);
    setError(null);
    setGcpMode(false);
    setGcpRows(makeDefaultGcps(icaoCode));
    resetPreview();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setResult(null);
    setError(null);
    resetPreview();
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped) {
      setFile(dropped);
      setResult(null);
      setError(null);
      resetPreview();
    }
  }

  async function handleImport() {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      let res: ImportTopologyResponse;
      if (activeTab === 'aip_pdf') {
        const gcpsForImport = gcpMode
          ? gcpRows
              .filter(r => r.pdfX !== null && r.pdfY !== null)
              .map(r => ({ pdf: [r.pdfX!, r.pdfY!] as [number, number], wgs84: r.wgs84 }))
          : undefined;
        res = await conopsService.importAipTopology(
          icaoCode, file, gcpsForImport,
          pageHint !== undefined ? pageHint - 1 : undefined,
        );
      } else if (activeTab === 'geojson') {
        res = await conopsService.importGeoJson(icaoCode, file);
      } else {
        res = await conopsService.importAixm(icaoCode, file);
      }
      setResult(res);
      onImported();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
      const msg = e?.response?.data?.detail || e?.message || 'Error al importar la topología.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  }

  // ── Cursor ────────────────────────────────────────────────────────────────
  const panelCursor =
    activeGcpIdx !== null ? 'cursor-crosshair' :
    isDragging            ? 'cursor-grabbing'  :
                            'cursor-grab';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div
        className={`relative w-full rounded-2xl border shadow-2xl overflow-hidden flex flex-col transition-all duration-300 max-h-[90vh] ${
          isWide ? 'max-w-6xl' : 'max-w-md'
        }`}
        style={{ background: '#141418', borderColor: '#2a2a30' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: '#2a2a30' }}>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#F26430]">{icaoCode}</p>
            <h2 className="text-base font-bold text-white leading-tight">Importar topología</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white">
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b flex-shrink-0" style={{ borderColor: '#2a2a30' }}>
          {(['aip_pdf', 'geojson', 'aixm'] as TabId[]).map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                activeTab === tab
                  ? 'text-[#F26430] border-b-2 border-[#F26430]'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className={`flex flex-1 min-h-0 ${isWide ? 'flex-row' : 'flex-col'}`}>

          {/* Left panel */}
          <div
            className={`p-5 space-y-4 overflow-y-auto flex-shrink-0 ${isWide ? 'w-80 border-r' : 'w-full'}`}
            style={isWide ? { borderColor: '#2a2a30' } : {}}
          >
            <p className="text-xs text-gray-400 leading-relaxed">{TAB_DESCRIPTIONS[activeTab]}</p>

            {/* Page hint */}
            {activeTab === 'aip_pdf' && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 flex-1">
                  Página <span className="text-gray-600">(opcional)</span>
                </span>
                <input
                  type="number" min={1} placeholder="Auto"
                  value={pageHint ?? ''}
                  onChange={(e) => setPageHint(e.target.value ? Number(e.target.value) : undefined)}
                  className="w-16 rounded-lg border border-[#2a2a30] bg-[#1e1e24] px-2 py-1 text-xs text-center text-white focus:outline-none focus:border-[#F26430]"
                />
              </div>
            )}

            {/* GeoJSON schema hint */}
            {activeTab === 'geojson' && (
              <div className="rounded-lg border border-[#2a2a38] bg-[#1a1a24] px-3 py-2 text-[11px] text-gray-500 leading-relaxed space-y-1">
                <p className="font-semibold text-gray-400">Propiedades requeridas por feature:</p>
                <p><span className="font-mono text-[#F26430]">aeroway</span> = runway · taxiway · apron · parking_position</p>
                <p><span className="font-mono text-gray-400">ref</span> = designador (ej: &quot;L&quot;, &quot;03R/21L&quot;) — opcional</p>
                <p><span className="font-mono text-gray-400">stand_number</span> = nº puesto — opcional, solo parking_position</p>
                <p><span className="font-mono text-gray-400">icao_cat</span> = B · C · D · E — opcional, solo parking_position</p>
              </div>
            )}

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-8 cursor-pointer transition-colors ${
                file
                  ? 'border-[#F26430]/60 bg-[#F26430]/5'
                  : 'border-[#2a2a30] hover:border-[#F26430]/40 hover:bg-white/5'
              }`}
            >
              <Icon size={36} weight="duotone" className={file ? 'text-[#F26430]' : 'text-gray-600'} />
              {file ? (
                <div className="text-center">
                  <p className="text-sm font-semibold text-white">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-gray-400">
                    Arrastra aquí o{' '}
                    <span className="text-[#F26430] font-semibold">selecciona archivo</span>
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {activeTab === 'aip_pdf' ? '.pdf' : activeTab === 'geojson' ? '.geojson / .json' : '.xml'}
                  </p>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept={accept} className="hidden" onChange={handleFileChange} />

            {/* GCP mode toggle */}
            {activeTab === 'aip_pdf' && (
              <div>
                <button
                  onClick={() => setGcpMode(v => !v)}
                  className="flex items-center gap-2 w-full py-2 text-xs text-gray-400 hover:text-gray-200 transition-colors"
                >
                  {gcpMode
                    ? <ToggleRight size={20} weight="fill" className="text-[#F26430]" />
                    : <ToggleLeft  size={20} className="text-gray-600" />
                  }
                  <span className={gcpMode ? 'text-[#F26430] font-semibold' : ''}>
                    Georreferenciación manual (Plano CAD)
                  </span>
                </button>

                {gcpMode && (
                  <div className="mt-2 space-y-3">
                    {/* Load preview */}
                    <button
                      onClick={loadPreview}
                      disabled={!file || previewLoading}
                      className="flex items-center justify-center gap-2 w-full py-2 rounded-lg border border-[#2a2a30] text-xs text-gray-300 hover:border-[#F26430]/50 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {previewLoading
                        ? <><CircleNotch size={13} className="animate-spin" />Renderizando plano…</>
                        : <><Eye size={13} />{previewLoaded ? 'Recargar vista previa' : 'Cargar vista previa'}</>
                      }
                    </button>

                    {/* GCP rows */}
                    <div className="space-y-1.5">
                      <p className="text-[10px] uppercase tracking-widest text-gray-600">
                        Puntos de control — {completedGcps} / 4
                        {previewLoaded && activeGcpIdx === null && (
                          <span className="ml-2 normal-case text-gray-700">selecciona una fila para armar</span>
                        )}
                      </p>
                      {gcpRows.map((row, i) => (
                        <button
                          key={i}
                          onClick={() => setActiveGcpIdx(prev => prev === i ? null : i)}
                          className={`w-full rounded-lg border px-3 py-2 text-left transition-all ${
                            activeGcpIdx === i
                              ? 'border-[#F26430]/60 bg-[#F26430]/10'
                              : row.pdfX !== null
                              ? 'border-green-600/30 bg-green-900/10'
                              : 'border-[#2a2a30] hover:border-[#3a3a40]'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] font-bold text-gray-200">{row.label}</span>
                            {row.pdfX !== null ? (
                              <CheckCircle size={12} weight="fill" className="text-green-400" />
                            ) : activeGcpIdx === i ? (
                              <Crosshair size={12} className="text-[#F26430] animate-pulse" />
                            ) : (
                              <MapPin size={12} className="text-gray-600" />
                            )}
                          </div>
                          <div className="text-[10px] text-gray-500 font-mono mt-0.5">
                            {row.wgs84[1].toFixed(6)}°N {row.wgs84[0].toFixed(6)}°E
                          </div>
                          {row.pdfX !== null ? (
                            <div className="text-[10px] text-green-500/70 font-mono">
                              PDF ({row.pdfX.toFixed(0)}, {row.pdfY?.toFixed(0)})
                            </div>
                          ) : activeGcpIdx === i ? (
                            <div className="text-[10px] text-[#F26430] mt-0.5">
                              Armado — haz clic en el plano →
                            </div>
                          ) : null}
                        </button>
                      ))}
                    </div>

                    {completedGcps > 0 && completedGcps < 4 && (
                      <p className="text-[10px] text-gray-600 text-center">
                        {4 - completedGcps} GCP{4 - completedGcps > 1 ? 's' : ''} restante{4 - completedGcps > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Result */}
            {result && (
              <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: '#22c55e33', background: '#0f2b1a' }}>
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-400" weight="fill" />
                  <span className="text-xs font-bold text-green-400">
                    Topología importada — {result.feature_count} features
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 pl-6">
                  {[
                    ['Calles rodaje', result.taxiway_count],
                    ['Pistas', result.runway_count],
                    ['Plataformas', result.apron_count],
                    ['Puestos', result.stand_count],
                  ].map(([label, count]) => (
                    <div key={label as string} className="flex items-center justify-between">
                      <span className="text-[10px] text-gray-500">{label}</span>
                      <span className="text-[10px] font-mono text-gray-300">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="rounded-xl border p-3 flex items-start gap-2" style={{ borderColor: '#ef535033', background: '#2b0f0f' }}>
                <WarningCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" weight="fill" />
                <p className="text-xs text-red-300 leading-relaxed">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              {result ? (
                <button
                  onClick={onClose}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold bg-green-600 hover:bg-green-500 text-white transition-colors"
                >
                  <CheckCircle size={16} weight="fill" />
                  Aceptar
                </button>
              ) : (
                <>
                  <button
                    onClick={onClose}
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-400 hover:text-white border border-[#2a2a30] hover:border-[#3a3a40] transition-colors"
                  >
                    Cerrar
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={!canImport || loading}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold bg-[#F26430] hover:bg-[#e05520] disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
                  >
                    {loading
                      ? <><CircleNotch size={16} className="animate-spin" />Importando…</>
                      : <><UploadSimple size={16} weight="bold" />Importar</>
                    }
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Right panel — always in DOM so canvasRef is available before previewLoaded.
               Visually hidden until isWide; the canvas must exist for PDF.js to render. */}
          <div className={`min-w-0 flex-col bg-[#08080c] ${isWide ? 'flex flex-1' : 'hidden'}`}>

              {/* Instruction banner */}
              <div className="flex-shrink-0 px-3 pt-3 pb-2">
                {activeGcpIdx !== null ? (
                  <div className="flex items-center gap-2 bg-[#F26430]/90 text-white px-3 py-2 rounded-lg text-xs font-semibold shadow-lg">
                    <Crosshair size={14} />
                    Armado: <strong className="ml-1">{gcpRows[activeGcpIdx]?.label}</strong>
                    <span className="ml-auto font-normal opacity-80">Haz clic en el plano · haz clic en la fila para desarmar</span>
                  </div>
                ) : completedGcps >= 4 ? (
                  <div className="flex items-center gap-2 bg-green-700/90 text-white px-3 py-2 rounded-lg text-xs font-semibold shadow-lg">
                    <CheckCircle size={14} weight="fill" />
                    Los 4 GCPs están definidos — haz clic en Importar
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-[#1e1e26] text-gray-500 px-3 py-2 rounded-lg text-xs">
                    <MapPin size={14} />
                    Selecciona un punto de control en el panel izquierdo para armarlo
                  </div>
                )}
              </div>

              {/* Canvas — zoom/pan container */}
              <div
                ref={containerRef}
                className={`flex-1 min-h-0 overflow-hidden select-none ${panelCursor}`}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {/* Transform wrapper (pan + zoom via CSS transform) */}
                <div
                  ref={imgWrapRef}
                  style={{ transformOrigin: '0 0', willChange: 'transform', display: 'inline-block' }}
                >
                  <canvas
                    ref={canvasRef}
                    onClick={handleGcpClickOnCanvas}
                    style={{ display: 'block', imageRendering: 'auto' }}
                  />
                </div>
              </div>

              {/* Zoom controls */}
              <div
                className="flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2 border-t"
                style={{ borderColor: '#1e1e24' }}
              >
                <button
                  onClick={() => zoomBy(1 / 1.3)}
                  title="Alejar"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <MagnifyingGlassMinus size={16} />
                </button>

                <span ref={zoomDisplayRef} className="text-[11px] font-mono text-gray-500 w-12 text-center">
                  100%
                </span>

                <button
                  onClick={() => zoomBy(1.3)}
                  title="Acercar"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <MagnifyingGlassPlus size={16} />
                </button>

                <div className="w-px h-4 bg-[#2a2a30] mx-1" />

                <button
                  onClick={fitToContainer}
                  title="Ajustar a ventana"
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <ArrowsOut size={14} />
                  Ajustar
                </button>

                <div className="ml-4 text-[10px] text-gray-700">
                  Rueda = zoom · Arrastrar = desplazar
                </div>
              </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopologyImportModal;
